import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Field, Header, Input, Message, Modal, Toast } from 'react-frontier';
import { bindClose, bindFormChange, getDepartmentName } from '../SiteUtil';
import { BudgetForm, EventDate, EventDateList, SetLoading, ExpenseType, Budget, Department } from '../SiteClasses';
import { Dropdown } from 'semantic-ui-react';
import { useUser } from '../hooks';
import SearchEventModal from './SearchEventModal';
import moment from 'moment';
import Validator from '../Validator';
import API from '../API';

interface BudgetModalProps{
	open: boolean;
	onClose: ()=>void;
	onCreate?: (budget: Budget)=>void;
	onUpdate?: (budget: Budget)=>void,
	date_id?: number;
	event_name?: string;
	budgetToEdit?: Budget;
}

var BudgetModal = (props: BudgetModalProps)=>{
	var { user } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [form, setForm] = useState<BudgetForm>(null);
	var [isEditing, setIsEditing] = useState<boolean>(false);
	var [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>(null);
	var [eventsModal, setEventsModal] = useState<boolean>(false);

	useEffect(()=>{
		if(props.open){
			var editingMode = Boolean(props.budgetToEdit);
			setIsEditing(Boolean(props.budgetToEdit));

			if(editingMode){
				setForm({
					date_id: props.date_id,
					type_id: props.budgetToEdit.type_id,
					department_id: props.budgetToEdit.department_id,
					expense_id: props.budgetToEdit.expense_id,
					budget_title: props.budgetToEdit.budget_title,
					date_deposit: props.budgetToEdit.date_deposit,
					amount: props.budgetToEdit.amount,
					description: props.budgetToEdit.description,
					event_name: props.event_name,
					use_date_id: !!props.date_id || !!props.budgetToEdit.date_id,
				});
			}else{
				setForm({
					date_id: props.date_id,
					type_id: null,
					department_id: null,
					expense_id: null,
					budget_title: null,
					date_deposit: null,
					amount: null,
					description: null,
					event_name: props.event_name,
					use_date_id: !!props.date_id,
				});
			}
			getTypes();
		}
	}, [props.open]);

	var getTypes = async ()=>{
		if(expenseTypes) return;
		API.getExpenseTypes().then(res=>{
			if(res.error) return setLoadError(res.message);
			return setExpenseTypes(res.data);
		}).catch(err=>{
			return setLoadError('Hubo un error al obtener los tipos. (GET-1)');
		})
	};

	var selectEvent = (event: EventDateList)=>{
		setForm(prev=>({
			...prev,
			date_id: event.date_id,
			event_name: event.event_name,
			use_date_id: true
		}));
		setEventsModal(false);
	};
	
	var submit = (setLoading: SetLoading)=>{
		var validationRules: any = {
			budget_title: [{
				rule: 'minLength', params: [3], label: 'Título de presupuesto'
			}],
			type_id: [{
				rule: 'empty', label: 'Tipo de gasto'
			}],
			department_id: [{
				rule: 'empty', label: 'Departamento'
			}],
			date_deposit: [{
				rule: 'number', label: 'Agendamiento de Pago'
			}],
			amount: [{
				rule: 'min', params: [1], label: 'Monto'
			}],
		};

		if(form.use_date_id){
			validationRules.date_id = [{
				rule: 'number', label: 'Evento'
			}];
		}

		var { valid, prompts } = Validator(form, validationRules);

		setModalPrompts(prompts);
		if(!valid) return;  
		setLoading(true);
		
		if(isEditing && props.budgetToEdit){
			API.editBudget(props.budgetToEdit.budget_id, form).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Presupuesto actualizado!');

				var updatedBudget: Budget = {
					...props.budgetToEdit,
					budget_title: form.budget_title,
					date_id: form.date_id,
					event_name: form.event_name,
					type_id: form.type_id,
					type_name: expenseTypes.find(t=>t.type_id === form.type_id)?.type_name,
					date_deposit: form.date_deposit,
					amount: form.amount,
					description: form.description,
					department_id: form.department_id,
				};
				props.onUpdate?.(updatedBudget);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado actualizando el presupuesto (LCL-EBU-2)']);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.createBudget(form).then(res=>{
				if(res.error) return setModalPrompts([res.message]);
				Toast.success('Presupuesto creado!');

				var newBudget: Budget = {
					budget_id: res.data.budget_id,
					budget_title: form.budget_title,
					date_id: form.date_id,
					event_name: form.event_name,
					type_id: form.type_id,
					type_name: expenseTypes.find(t=>t.type_id === form.type_id)?.type_name,
					date_deposit: form.date_deposit,
					amount: form.amount,
					description: form.description,
					date_created: moment().unix(),
					approved: res.data.approved,
					deleted: false,
					user_creator: user.user_id,
					user_creator_name: (user.first_name + ' ' +user.last_name),
					user_approved: res.data.approved ? user.user_id : null,
					user_approver_name: res.data.approved ? (user.first_name + ' ' +user.last_name) : null,
					department_id: form.department_id,
					reject_reason: null,
					reject_reason_name: null,
					expense_id: null,
					rejected: false,
				};
				props.onCreate(newBudget);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado creando el presupuesto (LCL-EBC-1)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var onFormChange = bindFormChange(form, setForm);

	return (
		<>
			<Modal open={props.open} onClose={props.onClose} size='tiny'>
				<Modal.Header>{isEditing ? 'Editar Presupuesto' : 'Crear Presupuesto'}</Modal.Header>
				<Modal.Content>
					{!form || !expenseTypes ? (
						loadError ? (
							<Header text='Error' subtext={loadError} />
						) : (
							<Header loading text='Cargando' />
						)
					) :  <>
							<Input label='Título' value={form.budget_title} onChange={onFormChange('budget_title')} />
							<>
								{!props.date_id ? <Checkbox 
									label='Vincular a evento'
									checked={form.use_date_id} 
									onChange={(v)=>{ setForm(prev=>({ ...prev, use_date_id: v, date_id: v ? prev.date_id : null, event_name: v ? prev.event_name : null })); }}
									style={{ marginBottom: 10, marginTop: 10 }}
								/> : null}
								{form.use_date_id && (
									<Field label='Evento' style={{ marginTop: 10 }}>
										<Input
											value={form.event_name}
											placeholder='Selecciona un evento...'
											readonly
											button={
												<Button
													text='Seleccionar'
													onClick={()=>setEventsModal(true)}
													disabled={!!props.date_id}
												/>
											}
										/>
									</Field>
								)}
							</>
							<Field label='Tipo de presupuesto'>
								<Dropdown
									selection
									value={form.type_id}
									onChange={onFormChange('type_id', true)}
									placeholder='Selecciona tipo'
									options={expenseTypes.map(t=>({
										text: t.type_name,
										value: t.type_id,
										key: t.type_id
									}))}
								/>
							</Field>						
							<Field label='Departamento'>
								<Dropdown
									selection
									value={form.department_id}
									onChange={onFormChange('department_id', true)}
									placeholder='Selecciona departamento'
									options={[
										{ key: Department.OPERACIONES, value: Department.OPERACIONES, text: 'Operaciones' },
										{ key: Department.COMERCIAL, value: Department.COMERCIAL, text: 'Comercial' },
										{ key: Department.DESARROLLO, value: Department.DESARROLLO, text: 'Desarrollo' },
										{ key: Department.RECURSOS_HUMANOS, value: Department.RECURSOS_HUMANOS, text: 'Recursos Humanos' },
										{ key: Department.MARKETING, value: Department.MARKETING, text: 'Marketing' },
										{ key: Department.CONTABILIDAD, value: Department.CONTABILIDAD, text: 'Contabilidad' },
									]}
								/>
							</Field>
							<Input label='Monto' placeholder='Monto presupuestado' value={form.amount} onChange={onFormChange('amount')} />
							<Input
								label='Agendamiento de Pago'
								value={form.date_deposit}
								onChange={onFormChange('date_deposit')}
								calendar={{
									date: form.date_deposit || moment().unix(),
									mode: 'date',
									format: 'DD/MM/YYYY'
								}}
							/>
							<Input label='Descripción' textarea value={form.description} onChange={onFormChange('description')} />
							<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
					</>}
				</Modal.Content>
				<Modal.Actions>
					<Button basic text='Cerrar' onClick={props.onClose} />
					<Button color="black" text={isEditing ? 'Guardar' : 'Crear'} onClick={submit}/>
				</Modal.Actions>
			</Modal>
			<SearchEventModal
				open={eventsModal}
				onClose={bindClose(setEventsModal, false)}
				onSelected={selectEvent}
			/>
		</>
	);
};

export default BudgetModal;
